/**
 * Chart.js HTML Legend Plugin
 *
 * Chart.js's built-in on-canvas legend has two problems in this app: it's drawn
 * with Chart.js's own font (never matches the app's real typography), and it has
 * no layout control — with more than ~3 datasets (SHM channels, compare-measurements'
 * unbounded series) it can eat most of the canvas height, squeezing the actual plot
 * down to a sliver.
 *
 * This plugin does NOT render anything itself (unlike Chart.js's documented HTML
 * legend sample, which writes DOM directly from the plugin). Instead it just calls
 * back into Angular with the same generateLabels() output the canvas legend would
 * have used, so ChartLegendComponent can render real Angular-templated DOM (real
 * *ngFor, real (click) bindings, real Tailwind classes) instead of hand-built
 * elements assigned via inline style.* in plain JS.
 *
 * Usage: set chart.options.plugins.legend.display = false (turns off the canvas
 * legend) and chart.options.plugins.htmlLegend.onLabelsChange = (items) => ...,
 * then register HtmlLegendPlugin in the chart's plugins array.
 */

export interface HtmlLegendItem {
  datasetIndex: number;
  text: string;
  fillStyle: string;
  strokeStyle: string;
  hidden: boolean;
}

export interface HtmlLegendOptions {
  onLabelsChange: (items: HtmlLegendItem[]) => void;
}

export const HtmlLegendPlugin = {
  id: 'htmlLegend',

  afterUpdate(chart: any) {
    const opts: HtmlLegendOptions | undefined = chart.options?.plugins?.htmlLegend;
    if (!opts?.onLabelsChange) return;

    const generate = chart.options?.plugins?.legend?.labels?.generateLabels;
    const rawItems = typeof generate === 'function' ? generate(chart) : [];

    const items: HtmlLegendItem[] = rawItems.map((item: any) => ({
      datasetIndex: item.datasetIndex,
      text: item.text,
      fillStyle: item.fillStyle,
      strokeStyle: item.strokeStyle,
      hidden: !!item.hidden,
    }));

    opts.onLabelsChange(items);
  },
};
